import React, { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { Icon } from "@iconify/react";
import Swal from "sweetalert2";
import {
    FaBox,
    FaShoppingBag,
    FaTruck,
    FaCreditCard,
    FaUser,
    FaHome,
    FaEnvelope,
    FaPhone,
    FaDownload,
    FaCheckCircle,
    FaClock,
    FaTimesCircle,
    FaReceipt,
    FaIdCard,
    FaCalendarAlt,
    FaMapPin,
    FaLocationArrow,
    FaSync,
    FaMoneyBillWave,
    FaShippingFast
} from "react-icons/fa";
import { fetchData } from "../helper/api.interceptor";
import "./order-full-details.css";

const OrderFullDetails = () => {
    const { orderId } = useParams();
    const [orderData, setOrderData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [updating, setUpdating] = useState(false);
    const navigate = useNavigate();

    useEffect(() => {
        const fetchOrderDetails = async () => {
            try {
                setLoading(true);
                const response = await fetchData.get(
                    `order/get-product-details/${orderId}`
                );

                if (response.status_code === 200) {
                    setOrderData(response.result);
                } else {
                    Swal.fire("Error", response.message || "Failed to load order data", "error");
                    navigate("/order_details");
                }
            } catch (error) {
                console.error(error);
                Swal.fire("Error", "Something went wrong", "error");
                navigate("/order_details");
            } finally {
                setLoading(false);
            }
        };

        if (orderId) {
            fetchOrderDetails();
        }
    }, [orderId, navigate]);

    const getStatusColor = (status) => {
        const colors = {
            "payment_received": "success",
            "on_delivery": "warning",
            "delivered": "info",
            "pending": "secondary",
            "cancelled": "danger"
        };
        return colors[status] || "primary";
    };

    const getStatusIcon = (status) => {
        const icons = {
            "payment_received": <FaMoneyBillWave className="text-success" />,
            "on_delivery": <FaTruck className="text-warning" />,
            "delivered": <FaCheckCircle className="text-info" />,
            "cancelled": <FaTimesCircle className="text-danger" />,
            "pending": <FaClock className="text-secondary" />
        };
        return icons[status] || <FaClock className="text-secondary" />;
    };

    const getStatusText = (status) => {
        const texts = {
            "payment_received": "Payment Received",
            "on_delivery": "On Delivery",
            "delivered": "Delivered",
            "cancelled": "Cancelled",
            "pending": "Pending"
        };
        return texts[status] || status;
    };

    const formatDate = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleDateString("en-IN", {
            day: "numeric",
            month: "short",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit"
        });
    };

    const downloadInvoice = async () => {
        try {
            Swal.fire({
                title: "Generating Invoice",
                text: "Please wait while we generate your invoice...",
                allowOutsideClick: false,
                didOpen: () => {
                    Swal.showLoading();
                }
            });

            setTimeout(() => {
                Swal.fire({
                    icon: "success",
                    title: "Invoice Downloaded",
                    text: "Your invoice has been downloaded successfully",
                });
            }, 1500);
        } catch (error) {
            console.error(error);
            Swal.fire("Error", "Failed to download invoice", "error");
        }
    };

    const updateOrderStatus = async (newStatus) => {
        try {
            const result = await Swal.fire({
                title: "Update Order Status",
                text: `Are you sure you want to update status to "${getStatusText(newStatus)}"?`,
                icon: "question",
                showCancelButton: true,
                confirmButtonColor: "#3085d6",
                cancelButtonColor: "#d33",
                confirmButtonText: "Yes, update it!"
            });

            if (result.isConfirmed) {
                setUpdating(true);
                const response = await fetchData.post("order/update-status", {
                    orderId: orderData.myOrderNumber,
                    orderStatus: newStatus
                });

                if (response.status_code === 200) {
                    setOrderData(prev => ({
                        ...prev,
                        orderStatus: newStatus,
                        updatedAt: new Date().toISOString()
                    }));
                    Swal.fire({
                        icon: "success",
                        title: "Status Updated",
                        text: `Order status has been updated to "${getStatusText(newStatus)}" successfully`,
                    });
                } else {
                    Swal.fire("Error", response.message || "Failed to update status", "error");
                }
            }
        } catch (error) {
            console.error(error);
            Swal.fire("Error", "Something went wrong while updating status", "error");
        } finally {
            setUpdating(false);
        }
    };

    if (loading) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ height: "80vh" }}>
                <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                </div>
            </div>
        );
    }

    if (!orderData) {
        return (
            <div className="text-center py-5">
                <h4>Order not found</h4>
                <p className="text-muted">The requested order could not be found.</p>
                <Link to="/order_details" className="btn btn-primary">
                    Back to Orders
                </Link>
            </div>
        );
    }

    const statusOptions = [
        { value: "payment_received", label: "Payment Received", icon: <FaMoneyBillWave /> },
        { value: "on_delivery", label: "On Delivery", icon: <FaShippingFast /> },
        { value: "delivered", label: "Delivered", icon: <FaCheckCircle /> }
    ];

    return (
        <div className="order-details-container">
            {/* ---------- HEADER ---------- */}
            <div className="page-header mb-4">
                <div className="d-flex flex-wrap align-items-center justify-content-between mb-3">
                    <div>
                        <h4 className="mb-1 fw-bold text-dark">Order Details</h4>
                        <p className="text-muted mb-0">Order #{orderData.myOrderNumber}</p>
                    </div>
                    <div className="d-flex gap-2">
                        <button
                            className="btn btn-outline-primary btn-sm d-flex align-items-center gap-2 px-3"
                            onClick={downloadInvoice}
                        >
                            <FaDownload size={14} />
                        </button>
                        <button
                            className="btn btn-outline-secondary btn-sm d-flex align-items-center gap-2 px-3"
                            onClick={() => window.print()}
                        >
                            <Icon icon="mdi:printer" />
                        </button>
                        <Link to="/order_details" className="btn btn-outline-dark btn-sm d-flex align-items-center gap-2 px-3">
                            <Icon icon="mdi:arrow-left" />
                        </Link>
                    </div>
                </div>
            </div>

            <div className="row g-4">
                {/* ---------- LEFT SIDE: CUSTOMER DETAILS ---------- */}
                <div className="col-lg-4">
                    {/* Customer Profile Card */}
                    <div className="card main-card mb-4">
                        <div className="card-header bg-white border-bottom">
                            <h5 className="card-title mb-0 d-flex align-items-center gap-2">
                                <FaUser className="text-primary" /> Customer Profile
                            </h5>
                        </div>
                        <div className="card-body">
                            <div className="customer-profile mb-4">
                                <div className="d-flex align-items-center mb-4">
                                    <div className="customer-avatar bg-primary-light rounded-circle d-flex align-items-center justify-content-center me-3 p-3">
                                        <FaUser className="text-primary" size={28} />
                                    </div>
                                    <div>
                                        <h5 className="mb-1 fw-bold">
                                            {orderData.billingDetails.firstName} {orderData.billingDetails.lastName}
                                        </h5>
                                        <small className="text-muted">Customer ID: {orderData.customerId}</small>
                                    </div>
                                </div>

                                <div className="contact-info mb-4">
                                    <h6 className="fw-semibold mb-3 d-flex align-items-center gap-2">
                                        <Icon icon="mdi:contact" /> Contact Information
                                    </h6>
                                    <div className="row g-2">
                                        <div className="col-12">
                                            <div className="contact-item d-flex align-items-center p-2 bg-light rounded">
                                                <FaPhone className="text-primary me-3" />
                                                <div>
                                                    <small className="text-muted d-block">Phone</small>
                                                    <span className="fw-semibold">{orderData.billingDetails.mobile}</span>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="col-12">
                                            <div className="contact-item d-flex align-items-center p-2 bg-light rounded">
                                                <FaEnvelope className="text-primary me-3" />
                                                <div>
                                                    <small className="text-muted d-block">Email</small>
                                                    <span className="fw-semibold">{orderData.billingDetails.email}</span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Billing Address */}
                            <div className="mb-4">
                                <h6 className="fw-semibold mb-3 d-flex align-items-center gap-2">
                                    <FaHome className="text-success" /> Billing Address
                                </h6>
                                <div className="address-card p-3 border rounded bg-light">
                                    <div className="d-flex align-items-start mb-2">
                                        <FaMapPin className="text-success mt-1 me-2" />
                                        <div>
                                            <p className="mb-1 fw-semibold">
                                                {orderData.billingDetails.firstName} {orderData.billingDetails.lastName}
                                            </p>
                                            <p className="mb-1 text-muted">address1 : {orderData.billingDetails.address1}</p>
                                            {orderData.billingDetails.address2 && (
                                                <p className="mb-1 text-muted">address2 : {orderData.billingDetails.address2}</p>
                                            )}
                                            <p className="mb-1 text-muted">
                                                city : {orderData.billingDetails.city}, {orderData.billingDetails.state}
                                            </p>
                                            <p className="mb-1 text-muted">
                                                state : {orderData.billingDetails.state}
                                            </p>
                                            <p className="mb-1 text-muted">PIN : {orderData.billingDetails.pin}</p>
                                            <p className="mb-0 text-muted">country : {orderData.billingDetails.country}</p>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Shipping Address */}
                            <div>
                                <h6 className="fw-semibold mb-3 d-flex align-items-center gap-2">
                                    <FaTruck className="text-warning" /> Shipping Address
                                </h6>
                                <div className="address-card p-3 border rounded bg-light">
                                    <div className="d-flex align-items-start mb-2">
                                        <FaLocationArrow className="text-warning mt-1 me-2" />
                                        <div>
                                            <p className="mb-1 fw-semibold">
                                                {orderData.shippingDetails.firstName} {orderData.shippingDetails.lastName}
                                            </p>
                                            <p className="mb-1 text-muted">address1 : {orderData.shippingDetails.address1}</p>
                                            {orderData.shippingDetails.address2 && (
                                                <p className="mb-1 text-muted">address2 : {orderData.shippingDetails.address2}</p>
                                            )}
                                            <p className="mb-1 text-muted">
                                                city : {orderData.shippingDetails.city}
                                            </p>
                                            <p className="mb-1 text-muted">
                                                state : {orderData.shippingDetails.state}
                                            </p>
                                            <p className="mb-1 text-muted">PIN: {orderData.shippingDetails.pin}</p>
                                            <p className="mb-0 text-muted">country : {orderData.shippingDetails.country}</p>
                                            {/* {orderData.billingDetails.pin === orderData.shippingDetails.pin ? (
                                                <span className="badge bg-success mt-2">Same as billing address</span>
                                            ) : (
                                                <span className="badge bg-info mt-2">Different shipping address</span>
                                            )} */}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* ---------- RIGHT SIDE: ORDER DETAILS ---------- */}
                <div className="col-lg-8">
                    {/* Order Summary Card */}
                    <div className="card main-card mb-4">
                        <div className="card-header bg-white border-bottom">
                            <div className="d-flex align-items-center justify-content-between">
                                <h5 className="card-title mb-0 d-flex align-items-center gap-2">
                                    <FaShoppingBag className="text-primary" /> Order Summary
                                </h5>
                                <span className="badge bg-primary">
                                    <FaReceipt className="me-1" /> {orderData.myOrderNumber}
                                </span>
                            </div>
                        </div>
                        <div className="card-body">
                            {/* Current Order Status */}
                            <div className="order-status-section mb-4">
                                <div className="row align-items-center">
                                    <div className="col-md-6">
                                        <div className="current-status mb-3 mb-md-0">
                                            <h6 className="text-muted mb-2">Current Status</h6>
                                            <div className="d-flex align-items-center gap-3">
                                                <div className={`status-badge status-badge-${getStatusColor(orderData.orderStatus)} d-flex align-items-center gap-2 px-3 py-2 rounded`}>
                                                    {getStatusIcon(orderData.orderStatus)}
                                                    <span className="fw-bold">{getStatusText(orderData.orderStatus)}</span>
                                                </div>
                                                <div className="status-update-time">
                                                    <small className="text-muted">
                                                        Last updated: {formatDate(orderData.updatedAt)}
                                                    </small>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="col-md-6">
                                        <div className="status-update-section">
                                            <h6 className="text-muted mb-2">Update Order Status</h6>
                                            <div className="d-flex gap-2">
                                                {statusOptions.map((status) => (
                                                    <button
                                                        key={status.value}
                                                        className={`btn ${orderData.orderStatus === status.value ? 'btn-primary' : 'btn-outline-primary'} btn-sm d-flex align-items-center gap-2 flex-grow-1 ${updating ? 'disabled' : ''}`}
                                                        onClick={() => updateOrderStatus(status.value)}
                                                        disabled={orderData.orderStatus === status.value || updating}
                                                    >
                                                        {updating && orderData.orderStatus === status.value ? (
                                                            <>
                                                                <span className="spinner-border spinner-border-sm" role="status"></span>
                                                                Updating...
                                                            </>
                                                        ) : (
                                                            <>
                                                                {status.icon}
                                                                {status.label}
                                                            </>
                                                        )}
                                                    </button>
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Order Info Cards */}
                            <div className="row mb-4">
                                <div className="col-md-3">
                                    <div className="info-card p-3 border rounded h-100">
                                        <small className="text-muted d-block mb-1">Order ID</small>
                                        <div className="d-flex align-items-center gap-2">
                                            <FaIdCard className="text-primary" />
                                            <span className="fw-bold">{orderData.orderId}</span>
                                        </div>
                                    </div>
                                </div>
                                <div className="col-md-3">
                                    <div className="info-card p-3 border rounded h-100">
                                        <small className="text-muted d-block mb-1">Transaction ID</small>
                                        <div className="d-flex align-items-center gap-2">
                                            <Icon icon="mdi:receipt-text" className="text-primary" />
                                            <span className="fw-bold">{orderData.transactionId}</span>
                                        </div>
                                    </div>
                                </div>
                                <div className="col-md-3">
                                    <div className="info-card p-3 border rounded h-100">
                                        <small className="text-muted d-block mb-1">Order Date</small>
                                        <div className="d-flex align-items-center gap-2">
                                            <FaCalendarAlt className="text-primary" />
                                            <span className="fw-bold">{new Date(orderData.createdAt).toLocaleDateString("en-IN")}</span>
                                        </div>
                                    </div>
                                </div>
                                <div className="col-md-3">
                                    <div className="info-card p-3 border rounded h-100">
                                        <small className="text-muted d-block mb-1">Last Updated</small>
                                        <div className="d-flex align-items-center gap-2">
                                            <FaCalendarAlt className="text-primary" />
                                            <span className="fw-bold">{new Date(orderData.updatedAt).toLocaleDateString("en-IN")}</span>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Products Section */}
                            <div className="mb-4">
                                <h6 className="section-title mb-3 d-flex align-items-center gap-2">
                                    <FaBox className="text-primary" /> Products Ordered
                                </h6>
                                <div className="table-responsive">
                                    <table className="table table-hover">
                                        <thead className="table-light">
                                            <tr>
                                                <th className="ps-3">Product Name</th>
                                                <th className="text-center">Unit Price</th>
                                                <th className="text-center">Quantity</th>
                                                <th className="text-center">Total Price</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {orderData.products.map((product, index) => (
                                                <tr key={index}>
                                                    <td className="ps-3">
                                                        <div className="d-flex flex-column">
                                                            <span className="fw-semibold">{product.productName} - {product.color_name}</span>
                                                            <small className="text-muted">Product ID: {product.productId}</small>
                                                        </div>
                                                    </td>
                                                    <td className="text-center">₹{product.price.toLocaleString("en-IN")}</td>
                                                    <td className="text-center">
                                                        <span className="badge bg-primary rounded-pill px-3 py-1">
                                                            {product.quantity}
                                                        </span>
                                                    </td>
                                                    <td className="text-center fw-bold text-primary">
                                                        ₹{(product.price * product.quantity).toLocaleString("en-IN")}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>


                        </div>
                    </div>

                    {/* Payment Details Card */}
                    <div className="card main-card">
                        <div className="card-header bg-white border-bottom">
                            <h5 className="card-title mb-0 d-flex align-items-center gap-2">
                                <FaCreditCard className="text-success" /> Payment Information
                            </h5>
                        </div>
                        <div className="card-body">
                            <div className="row mb-4">
                                <div className="col-md-4">
                                    <div className="payment-card text-center p-4 border rounded">
                                        <div className="payment-icon mb-3">
                                            <Icon icon="mdi:truck-delivery" className="text-info fs-1" />
                                        </div>
                                        <small className="text-muted d-block">Delivery Charges</small>
                                        <h4 className="fw-bold mb-0">
                                            ₹{orderData.deliveryAmount.toLocaleString("en-IN")}
                                        </h4>
                                    </div>
                                </div>
                                <div className="col-md-4">
                                    <div className="payment-card text-center p-4 border rounded">
                                        <div className="payment-icon mb-3">
                                            <Icon icon="mdi:discount" className="text-warning fs-1" />
                                        </div>
                                        <small className="text-muted d-block">Discount Applied</small>
                                        <h4 className="text-success fw-bold mb-0">
                                            ₹{orderData.discountAmount.toLocaleString("en-IN")}
                                        </h4>
                                    </div>
                                </div>
                                <div className="col-md-4">
                                    <div className="payment-card text-center p-4 border rounded">
                                        <div className="payment-icon mb-3">
                                            <Icon icon="mdi:cash" className="text-success fs-1" />
                                        </div>
                                        <small className="text-muted d-block">Total Amount</small>
                                        <h4 Amount className="text-primary fw-bold mb-0">
                                            ₹{orderData.totalAmount.toLocaleString("en-IN")}
                                        </h4>
                                    </div>
                                </div>


                            </div>

                            <div className="row">
                                <div className="col-md-6">
                                    <div className="payment-details p-3 border rounded">
                                        <h6 className="fw-semibold mb-3">Payment Details</h6>
                                        <div className="detail-item d-flex justify-content-between mb-2">
                                            <span className="text-muted">Payment Method:</span>
                                            <span className="fw-semibold">Online Payment</span>
                                        </div>
                                        <div className="detail-item d-flex justify-content-between mb-2">
                                            <span className="text-muted">Payment Status:</span>
                                            <span className="badge bg-success">Completed</span>
                                        </div>
                                        <div className="detail-item d-flex justify-content-between mb-2">
                                            <span className="text-muted">Transaction Date:</span>
                                            <span className="fw-semibold">{formatDate(orderData.createdAt)}</span>
                                        </div>
                                    </div>
                                </div>
                                <div className="col-md-6">
                                    <div className="transaction-details p-3 border rounded">
                                        <h6 className="fw-semibold mb-3">Transaction Details</h6>
                                        <div className="detail-item d-flex justify-content-between mb-2">
                                            <span className="text-muted">Transaction ID:</span>
                                            <span className="fw-semibold">{orderData.transactionId}</span>
                                        </div>
                                        <div className="detail-item d-flex justify-content-between mb-2">
                                            <span className="text-muted">Order ID:</span>
                                            <span className="fw-semibold">{orderData.orderId}</span>
                                        </div>
                                        <div className="detail-item d-flex justify-content-between">
                                            <span className="text-muted">Customer ID:</span>
                                            <span className="fw-semibold">{orderData.customerId}</span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default OrderFullDetails;